import moment from "moment";
import { z } from "zod";
import { YayWebSocket } from "../web-socket/webSocket";
import NebualApi, { CACHE_CHAT_ADDR, nebulaServerOrDefault } from "./neulaApi";
import { SocketBackoff } from "../SocketBackoff";
import { AppDispatch, EVENT_SOCKET_STATUS } from "../store";
import { chatNSlice, handleConversationLeft, handleNewConversation, receivedConversationMessage } from "../slices/chatNSlice";
import { chatMessageSchema, lastMessageSchema } from "./nebulaChat";
import { receivedFileLoaded, receivedMessageUpdated, updateOneMessage } from "../slices/messageSlice/thunks";
import { SocketStatus } from ".";
import { messageSlice } from "../slices/messageSlice";

const NEBULA_CHAT_WS_URL = nebulaServerOrDefault(CACHE_CHAT_ADDR, true).replace('http', 'ws');

const userTypingSchema = z.object({
    is_typing: z.boolean(),
    to: z.string(),
    to_type: z.string(),
    user: z.string(),
});
export type UserTyping = z.infer<typeof userTypingSchema>

const chatUserUpdatedSchema = z.object({
    to: z.string(),
    to_type: z.string(),
    is_muted: z.boolean(),
    is_favourite: z.boolean(),
    is_archived: z.boolean(),
});
export type ChatUserUpdated = z.infer<typeof chatUserUpdatedSchema>;

const memberReadSchema = z.object({
    last_delivered_at: z.string().nullish(),
    last_read_at: z.string().nullish(),
    to: z.string(),
    to_type: z.string(),
    user: z.string(),
    is_read: z.boolean(),
})
export type MemberReadStatus = z.infer<typeof memberReadSchema>;

const fileLoadedSchema = z.object({
    file_uuid: z.string(),
    message_uuid: z.string(),
    metadata: z.any().nullish(),
    to: z.string(),
    to_type: z.string(),
});
export type NebulaFileLoaded = z.infer<typeof fileLoadedSchema>;


const reactionsUpdatedSchema = z.object({
    to: z.string(),
    to_type: z.string(),
    message_uuid: z.string(),
    reactions: z.array(z.object({
        uuid: z.string(),
        content: z.string(),
        time: z.string(),
        user: z.string(),
    })),
});

export interface SendMessageReactionParams {
    to: string,
    to_type: string,
    message_uuid: string,
    content: string,
}

export interface DeleteMessageReactionsParams {
    to: string,
    to_type: string,
    message_uuid: string,
    uuid: string,
}

const nebulaMessageUpdatedSchema = z.object({
    to: z.string(),
    to_type: z.string(),
    message_uuid: z.string(),
    content: z.string().nullish()
});

const nebulaMessageDeletedSchema = z.object({
    to: z.string(),
    to_type: z.string(),
    message_uuid: z.string(),
});

const channelLeftSchema = z.object({
    to: z.string(),
    to_type: z.string(),
});

const channelParticipantsSchema = z.object({
    to: z.string(),
    to_type: z.string(),
    members: z.array(z.object({
        uuid: z.string(),
        member: z.string(),
        external: z.boolean(),
        joined_at: z.string(),
        last_read_at: z.string().nullish(),
        last_delivered_at: z.string().nullish(),
    })),
});

export class NebulaChatSocket {
    private sipName: string;

    private sipPassword: string;

    public authUntil: moment.Moment;

    private authToken: string | null = null;

    private socket: YayWebSocket | null = null;

    private socketBackoff = new SocketBackoff();

    private isExplicitlyClosed = false;

    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        sipName: string,
        sipPassword: string,
    ) {
        this.sipName = sipName;
        this.sipPassword = sipPassword;
        this.authUntil = moment.utc();
    }

    public getAuthToken(fresh = false) {
        const now = moment.utc();

        if (!fresh && this.authToken && this.authUntil.isAfter(now)) {
            const pushToken = this.authToken;
            return new Promise<string>((resolve) => {
                resolve(pushToken);
            })
        }

        return new Promise<string>((resolve, reject) => {
            this.generateAuthToken()
                .then(newtoken => {
                    this.authToken = newtoken;
                    this.authUntil = moment.utc().add(10, 'minutes');
                    resolve(newtoken);
                })
                .catch(e => {
                    console.error("Failed to generate token", e);
                    reject(e);
                })
        })
    }

    public close(code: number, message: string) {
        this.isExplicitlyClosed = true;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.socket) {
            this.socket.close(code, message);
        }
    }

    private generateAuthToken() {
        return new Promise<string>((resolve, reject) => {
            NebualApi.login(this.sipName, this.sipPassword)
                .then(login => {
                    resolve(login.token)
                })
                .catch(e => {
                    reject(e)
                })
        })
    }

    public start(dispatch: AppDispatch, userUuid: string, onConnected?: () => void) {
        this.isExplicitlyClosed = false;

        return this.getAuthToken(true).then(token => {
            const ws = new YayWebSocket(`${NEBULA_CHAT_WS_URL}/v1/chat/ws?meta=chat&token=${token}`, []);

            ws.set_auth_status(1);

            ws.onConnect = () => {
                console.debug("Connected to Nebula Chat");
                window.dispatchEvent(new Event(EVENT_SOCKET_STATUS));

                NebulaChatSocket.handleSubscribe(dispatch, ws, userUuid);

                setTimeout(() => {
                    ws.ping();
                }, 1_000);

                onConnected?.();
            };

            ws.onClose = () => {
                window.dispatchEvent(new Event(EVENT_SOCKET_STATUS));

                if (this.isExplicitlyClosed || !window.navigator.onLine) return;

                const backoffMs = this.socketBackoff.getNextBackoff();
                this.reconnectTimer = setTimeout(() => {
                    this.reconnectTimer = null;
                    this.connect();
                }, backoffMs);
            };

            this.socket = ws;

            return this.connect();
        });
    }

    public connect(seq = 0): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.isExplicitlyClosed) {
                reject(new Error("Socket explicitly closed"));
                return;
            }

            if (!this.socket) {
                reject(new Error("Socket not initialized"));
                return;
            }

            this.socket.connect();

            setTimeout(() => {
                if (this.isExplicitlyClosed) {
                    reject(new Error("Socket explicitly closed"));
                    return;
                }

                if (this.socket?.check_is_connected()) {
                    resolve();
                    return;
                }

                const backoffMs = this.socketBackoff.getNextBackoff();
                this.reconnectTimer = setTimeout(() => {
                    this.reconnectTimer = null;
                    this.connect(seq + 1).then(resolve).catch(reject);
                }, backoffMs);
            }, 1_000);
        });
    }

    public force_reconnect() {
        if (!this.socket) return;
        this.socketBackoff.reset();
        this.isExplicitlyClosed = false;
        this.socket.force_connect();
    }

    public check_connection_status(): SocketStatus {
        if (!this.socket) return 'no_connection';

        if (!this.socket.check_is_connected()) return 'no_connection';

        if (this.socket.get_auth_status() !== 1) return "no_auth";

        return 'ok';
    }

    public async notifyTyping(typing: UserTyping): Promise<any> {
        if (!this.socket || !this.socket.check_is_connected()) {
            return null;
        }

        return this.socket.call("notify_typing", typing, { onOne: true });
    }

    public async updateChatMember(update: ChatUserUpdated): Promise<any> {
        if (!this.socket || !this.socket.check_is_connected()) {
            return null;
        }

        return this.socket.call("update_chat_member", update, { onOne: true });
    }

    public async updateChatReadStatus(to: string, to_type: string, is_read: boolean): Promise<any> {
        if (!this.socket || !this.socket.check_is_connected()) {
            return null;
        }

        return this.socket.call('update_message_status', {
            to,
            to_type,
            is_read
        }, { onOne: true })
    }

    public async sendReaction(params: SendMessageReactionParams): Promise<any> {
        if (!this.socket || !this.socket.check_is_connected()) {
            return null;
        }

        return this.socket.call('send_message_reaction', params, { onOne: true })
    }

    public async deleteReaction(params: DeleteMessageReactionsParams): Promise<any> {
        if (!this.socket || !this.socket.check_is_connected()) {
            return null;
        }

        return this.socket.call('delete_message_reaction', params, { onOne: true })
    }

    private static handleSubscribe(dispatch: AppDispatch, socket: YayWebSocket, userUuid: string) {
        socket.subscribe('notify_message', (data) => {
            const validated = chatMessageSchema.safeParse(data.params);

            if (!validated.success) {
                console.error("Failed to parse incoming message", validated.error);
            } else {
                const message = validated.data;
                dispatch(receivedConversationMessage(message, (isActive: boolean) => {
                    socket.call('update_message_status', {
                        to: message.to,
                        to_type: message.to_type,
                        is_read: isActive
                    }, { onOne: true })
                }));
            }
        });

        socket.subscribe('notify_message_deleted', (data) => {
            const validated = nebulaMessageDeletedSchema.safeParse(data.params);

            if (!validated.success) {
                console.error("Failed to parse message deleted", validated.error);
            } else {
                dispatch(messageSlice.actions.deleteOneMessage({ message_uuid: validated.data.message_uuid, chat_uuid: validated.data.to }));

                // Clean up if thread existed on message
                dispatch(
                    handleConversationLeft(validated.data.message_uuid)
                );
            }
        });

        socket.subscribe('notify_message_edited', (data) => {
            const validated = nebulaMessageUpdatedSchema.safeParse(data.params);

            if (!validated.success) {
                console.error("Failed to parse message updated")
            } else {
                dispatch(receivedMessageUpdated(validated.data.to, validated.data.message_uuid, (isActive: boolean) => {
                    socket.call('update_message_status', {
                        to: validated.data.to,
                        to_type: validated.data.to_type,
                        is_read: isActive
                    }, { onOne: true })
                }))
            }
        });

        socket.subscribe('notify_typing', (data) => {
            const validated = userTypingSchema.safeParse(data.params);
            if (!validated.success) {
                console.error("Failed to parse notify typing")
            } else if (validated.data.user !== userUuid) {
                dispatch(chatNSlice.actions.updateTypingUser(validated.data));
            }
        });

        socket.subscribe('notify_member_edit', (data) => {
            const validated = chatUserUpdatedSchema.safeParse(data.params);
            if (validated.success) {
                dispatch(chatNSlice.actions.updateLocalChatMember(validated.data));
            }
        });

        socket.subscribe('notify_file_loaded', (data) => {
            const validated = fileLoadedSchema.safeParse(data.params);
            if (!validated.success) {
                console.error("Failed to parse notify file loaded")
            } else {
                dispatch(receivedFileLoaded(validated.data));
            }
        });

        socket.subscribe('notify_member_read', (data) => {
            const validated = memberReadSchema.safeParse(data.params);

            if (!validated.success) {
                console.error("Failed to parse notify_member_read", validated);
            } else {
                dispatch(chatNSlice.actions.updateMemberRead(validated.data));

                if (validated.data.is_read && validated.data.user === userUuid) {
                    dispatch(chatNSlice.actions.markConversationRead({ conversationUuid: validated.data.to }));
                }
            }
        });

        socket.subscribe('notify_reaction_update', (data) => {
            const validated = reactionsUpdatedSchema.safeParse(data.params);

            if (validated.success) {
                dispatch(
                    updateOneMessage({
                        chat_uuid: validated.data.to,
                        message_uuid: validated.data.message_uuid,
                        changes: { reactions: validated.data.reactions }
                    })
                );
            }
        });

        socket.subscribe('notify_channel_added', (data) => {
            const validated = lastMessageSchema.safeParse(data.params);
            if (validated.success) {
                dispatch(handleNewConversation(validated.data));
            }
        });

        socket.subscribe('notify_channel_left', (data) => {
            const validated = channelLeftSchema.safeParse(data.params);
            if (validated.success) {
                dispatch(
                    handleConversationLeft(validated.data.to)
                );
            }
        });

        socket.subscribe('notify_channel_participants', (data) => {
            const validated = channelParticipantsSchema.safeParse(data.params);
            if (validated.success) {
                dispatch(
                    chatNSlice.actions.setChatParticipants({
                        chat_uuid: validated.data.to,
                        participants: validated.data.members
                    })
                );
            }
        });
    }
}
